<script lang="ts">
  import Card from '@smui/card';
  import type { Options } from '@splidejs/splide';
  import { Splide, SplideSlide } from '@splidejs/svelte-splide';
  import { onMount } from 'svelte';
  import { applyRTLArrowCss, isDirectionRTL } from '../../../common/util';
  import { getImageAltText, getStyle, showTileInfo } from '../services/pdp-data-services';

  export let keyHighlights;

  const isRTL = isDirectionRTL();

  const slideOptions: Options = {
    type: 'slide',
    arrows: keyHighlights?.length > 5 ? true : false,
    pagination: false,
    direction: (isRTL && 'rtl') || 'ltr',
    gap: '12px',
    autoWidth: true,
    fixedHeight: '80px',
    releaseWheel: true,
    cover: true,
    updateOnMove: true,
    breakpoints: {
      700: {
        gap: '8px',
        perPage: 3,
      },
    },
    classes: {
      arrow: ' splide__arrow splide_arrow-pdpbg',
      prev: 'splide__arrow--prev splide_arrow-prev',
      next: 'splide__arrow--next splide_arrow-next',
    },
  };

  onMount(() => {
    const parent = document.querySelectorAll('.pdp-main-tile-container');
    applyRTLArrowCss(parent, 'auto', 0, 0, 'auto');
  });
</script>

{#if keyHighlights != null && keyHighlights.length > 0}
  <div class="pdp-main-tile-container">
    <Splide options={{ ...slideOptions }}>
      {#each keyHighlights as slide, i}
        <SplideSlide>
          <div class="card-container" on:click={() => showTileInfo(i)} on:keydown={() => showTileInfo(i)}>
            <Card class={slide.icon ? 'with-icon' : 'without-icon'} style={`background-color:${slide.background};`}>
              {#if slide.backgroundImage}
                <img
                  class="card-background-image"
                  src={slide.backgroundImage}
                  alt={getImageAltText(slide.backgroundImage)}
                />
              {/if}
              <div class="inner-details">
                {#if slide.icon != null}
                  <div class="inner-details-left">
                    <!-- <Icon glyph={slide.icon} size={10} /> -->
                    <img class="inner-details-icon" src={slide.icon} alt="icon" />
                  </div>
                {/if}
                <div class="inner-details-right">
                  {#if slide.title != null}
                    <div class="mdc-typography--headline6" style={getStyle(slide?.titleColorCode, 'title')}>
                      {slide?.title}
                    </div>
                  {/if}
                  {#if slide.description != null}
                    <div class="mdc-typography--caption" style={getStyle(slide?.descriptionColorCode, 'description')}>
                      {slide?.description}
                    </div>
                  {/if}
                </div>
              </div>
            </Card>
          </div>
        </SplideSlide>
      {/each}
    </Splide>
  </div>
{/if}

<style lang="scss">
  @import '../../common/styles/colors.new.scss';

  .pdp-main-tile-container {
    // todo: add mobile styles here

    @include desktop-min-width {
      padding: 24px 48px 0px 120px;
    }
  }

  :global(.pdp-main-tile-container .mdc-card) {
    position: relative;
    overflow: hidden;
  }

  .card-background-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    z-index: 0;
  }

  .inner-details {
    position: relative;
    z-index: 1;
  }

  .pdp-main-tile-container .card-container {
    cursor: pointer;
  }

  .inner-details {
    padding: 12px 10px;
    display: flex;
    justify-content: flex-start;
    text-align: left;
    align-items: flex-start;
    gap: 8px;
    @include desktop-max-width {
      flex-direction: column;
    }
    .inner-details-left {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      width: 30px;
      margin-top: 2px;
      .inner-details-icon {
        max-width: 30px;
      }
    }
    .inner-details-right {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: start;
    }
  }
  .pdp-main-tile-container {
    * :global(.mdc-card) {
      height: 80px;
      width: fit-content;
      border-radius: 12px;
    }

    :global(.mdc-card.with-icon) {
      width: 174px;
    }

    :global(.mdc-card.without-icon) {
      width: 136px;
    }
  }

  .pdp-main-tile-container :global {
    .splide__arrow svg {
      fill: white !important;
      width: 15px !important;
      height: 18px !important;
    }
    .splide {
      margin: 24px 0 64px 48px;
    }

    .splide__arrows {
      display: none;
    }
    .splide--rtl {
      direction: ltr !important;
    }

    .splide:hover .splide__arrows {
      display: block;
    }

    .splide:hover .splide_arrow-pdpbg {
      background: $color-bg-black;
      height: 80px;
      border-radius: 0;
      opacity: 48%;
      color: $primary-white;
      width: 36px;
      &:disabled {
        display: none;
      }
    }
    .splide__slide:first-child {
      margin-left: 0px !important;
    }
  }
  .mdc-typography--headline6 {
    font-weight: 700;
    font-size: 20px;
    line-height: 22px;
    margin-bottom: 2px;
  }
  .mdc-typography--caption {
    font-weight: 400;
    font-size: 14px;
    line-height: 16px;
  }

  @media (min-device-width: 320px) and (max-device-width: 480px) {
    * :global(.mdc-card) {
      height: 80px;
      background: linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)),
        linear-gradient(0deg, rgba(69, 0, 158, 0.5), rgba(69, 0, 158, 0.5)), url(.jpg);
      border-radius: 12px;
    }
    .mdc-typography--headline6 {
      font-weight: 700;
      font-size: 14px;
      line-height: 16px;
      margin-bottom: 2px;
    }
    .mdc-typography--caption {
      font-weight: 400;
      font-size: 11px;
      line-height: 12px;
    }
  }
</style>
